import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_PRODUCTS, NAATI_PRODUCT_CATEGORIES, NAATI_PRICE_MIN_THB, NAATI_PRICE_MAX_THB, NAATI_TURNAROUND_MIN, NAATI_TURNAROUND_MAX } from "@/data/naati-products";
import { BRAND_URL } from "@/lib/brand";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const TITLE_TH = "สินค้า NAATI แปลรับรองทุกประเภท | 1,300–2,500 บาท 5–7 วัน | NYC";
const DESC_TH = "แค็ตตาล็อกบริการแปล NAATI 28 ประเภทเอกสาร ครอบคลุมทะเบียนราษฎร์ การศึกษา การเงิน กฎหมาย ระยะเวลา 5–7 วันทำการ — ปรึกษาฟรี ทักไลน์/โทรได้ทันที";

export const Route = createFileRoute("/naati/products/")({
  head: () => {
    const url = `${BRAND_URL}/naati/products`;
    return {
      meta: [
        { title: ctaTitle(TITLE_TH, "/naati/products/index", "th")}, { name: "description", content: ctaDesc(DESC_TH, "/naati/products/index", "th")},
        { property: "og:title", content: ctaTitle(TITLE_TH, "/naati/products/index", "th")}, { property: "og:description", content: ctaDesc(DESC_TH, "/naati/products/index", "th")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: `${BRAND_URL}/en/naati/products` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "CollectionPage", "@id": `${url}#collection`,
            url, name: TITLE_TH, inLanguage: "th-TH",
            numberOfItems: NAATI_PRODUCTS.length,
          }, {
            "@type": "ItemList", "@id": `${url}#itemlist`,
            itemListElement: NAATI_PRODUCTS.map((p, i) => ({
              "@type": "ListItem", position: i + 1,
              url: `${BRAND_URL}/naati/products/${p.slug}`, name: p.nameTh,
            })),
          }, {
            "@type": "OfferCatalog", "@id": `${url}#catalog`, name: TITLE_TH,
            itemListElement: NAATI_PRODUCTS.map((p) => ({
              "@type": "Offer", name: p.nameTh, sku: p.sku,
              priceCurrency: "THB", price: 1900,
              url: `${BRAND_URL}/naati/products/${p.slug}`,
            })),
          }],
        }),
      }],
    };
  },
  component: CatalogTH,
});

function CatalogTH() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-amber-100/60 to-background px-4 pt-16 pb-10">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-700">NAATI Product Catalog</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">สินค้าบริการแปล NAATI 28 ประเภทเอกสาร</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-amber-400 bg-amber-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
            ทุกประเภทราคา THB {NAATI_PRICE_MIN_THB.toLocaleString()}–{NAATI_PRICE_MAX_THB.toLocaleString()} · ส่งใน {NAATI_TURNAROUND_MIN}–{NAATI_TURNAROUND_MAX} วันทำการ · รับรองโดยนักแปล NAATI CPN + ทนายไทยตรวจก่อนประทับตราทุกฉบับ
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-10">
        {NAATI_PRODUCT_CATEGORIES.map((c) => (
          <div key={c.id} className="mb-10">
            <h2 className="mb-4 font-display text-xl font-extrabold text-navy-deep">
              {c.labelTh} <span className="text-sm font-normal text-slate-500">({c.count} รายการ)</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {NAATI_PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                <Link key={p.sku} to="/naati/products/$sku" params={{ sku: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{p.sku}</div>
                  <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-amber-600">{p.nameTh}</div>
                  <div className="mt-2 text-xs text-slate-600 line-clamp-2">{p.shortDescTh}</div>
                  <div className="mt-3 text-xs font-semibold text-emerald-700">THB {NAATI_PRICE_MIN_THB.toLocaleString()}–{NAATI_PRICE_MAX_THB.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
      <ProductCatalogHubNav locale="th" current="naati" />
    <HubLeadCTA locale="th" service="NAATI Certified Translation" />
      </main>
  );
}
